//=============================================
// ARCHELIOS MAP NAMESPACE
//=============================================
var am = (function(module) {

    //Use strict mode
    'use strict';

    /**
     * Class simulating a thermal solar panel installation
     */
    module.ThermalPanelInstallation = function() {
        // Private attributes
        this._roofModel = undefined; // reference to the object computing the solar potential of the selected roof sides
        this._params = undefined;    // parameters
        this._properties = {};
    };

    /**
     * Class constructor
     * @constructor
     *
     * @param {am.ModelBaseClass} roofModel - Reference to the SolarPotentialModel instance to be used in order to 
     *      compute the solar potential of the PV panel installation.
     * @param {Object} params - Object configuration. The following keys are required:
     *      - hotWaterPanelPurchasePrice: [euros/m2] Price of a hot water solar panel
     *      - annualBoilerRunningCost: [euros/pers.] Annual cost for running the boiler
     *      - hotWaterConsumptionPerInhabitant: [L] daily hot water consumption per inhabitant
     *
     */
    module.ThermalPanelInstallation.prototype.init = function(roofModel, params) {
        this._roofModel = roofModel;
        this._params = params;
        this._params.numberOfDaysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

        // Define the surface of thermal solar panels to be installed per inhabitant
        if (this._params.solarPanelSurfacePerInhabitant === undefined) {
            this._params.solarPanelSurfacePerInhabitant = 0.25; // m²
        }

        // Compute the annual mean, annual min and annual max air temperatures (in degrees Celsius)
        var add = function(a, b) { return a + b; };
        var annualMeanAirTemp = this._params.annualMeanAirTemperature = params.monthlyMeanAirTemperature.reduce(add) / 12;
        var minAnnualAirTemp = Math.min.apply(Math, params.monthlyMeanAirTemperature); // finds the min of array params.monthlyMeanAirTemperature 
        var maxAnnualAirTemp = Math.max.apply(Math, params.monthlyMeanAirTemperature); // finds the max of array params.monthlyMeanAirTemperature

        // Compute the monthly mean energy requirements to heat the water for one person (in kWh)
        this._params.monthlyMeanEnergyRequirements = [];
        var waterHeatCapacity = 4185 / 3600; // in kWh/m3/K
        var airTemperatureAmplitude = Math.max(annualMeanAirTemp - minAnnualAirTemp, maxAnnualAirTemp - annualMeanAirTemp);
        var phaseShift = 30; //days
        var k = Math.sqrt(2 * Math.PI / (8760 * 3600 * 2 * params.soilThermalDiffusivity));
        var numberOfDaysSinceYearStart = this._params.numberOfDaysPerMonth[0] / 2;
        for (var iMonth = 0; iMonth < 12; ++iMonth) {
            var monthlyHotWaterConsumptionPerInhabitant = this._params.dailyHotWaterConsumptionPerInhabitant * this._params.numberOfDaysPerMonth[iMonth] / 1000; // in m3
            var coldWaterTemperature = annualMeanAirTemp + airTemperatureAmplitude * Math.exp(-k) * Math.sin(2 * Math.PI * (numberOfDaysSinceYearStart - phaseShift) / 365 - Math.PI / 2 - k);
            this._params.monthlyMeanEnergyRequirements.push(monthlyHotWaterConsumptionPerInhabitant * waterHeatCapacity * (this._params.waterHeatingTemperature - coldWaterTemperature));
            if (iMonth != 11) {
                numberOfDaysSinceYearStart += (this._params.numberOfDaysPerMonth[iMonth] + this._params.numberOfDaysPerMonth[iMonth + 1]) / 2;
            }
        }

        // Compute the annual mean energy requirements to heat the water for one person (in kWh)
        this._params.annualMeanEnergyRequirements = this._params.monthlyMeanEnergyRequirements.reduce(add);

        // Compute the factor to be multiplied with the annual solar potential in order to get the monthly potential
        this._params.annualToMonthlySolarPotential = [];
        var hourlyCoeffs = params.profiles.prod;
        var iCoeffStart = 0;
        for (var iMonth = 0; iMonth < 12; ++iMonth) {
            var iCoeffEnd = iCoeffStart + this._params.numberOfDaysPerMonth[iMonth] * 24;
            var coeffs = hourlyCoeffs.values.slice(iCoeffStart, iCoeffEnd);
            this._params.annualToMonthlySolarPotential.push(coeffs.reduce(add) / hourlyCoeffs.somme);
            iCoeffStart = iCoeffEnd;
        }
    };

    /**
     * Class destructor
     */
    module.ThermalPanelInstallation.prototype.destroy = function() {
        this._roofModel = undefined;
        this._params = undefined;
    };

    /**
     * Computes the characteristics of the thermal panel installation. The surface of the thermal solar panels is computed so as
     * to optimize the discounted net profit at the end of the observation period.
     *
     * @param {Object} userInfos - Information about the (future) owner of the thermal installation. This object must 
     *      contain the following keys:
     *          - userCategory: [individual|professionnal|administration] User category
     *          - hasElectricalBoiler: [true|false] Is the building equipped with an electrical boiler?
     *          - numberOfInhabitants: [-] Number of inhabitants in the building
     */
    module.ThermalPanelInstallation.prototype.computeProperties = function(userInfos) {
        // Compute the surface of solar panels to be installed and the number of inhabitants in the building
        var panelSurface, nbInhabitants;
        if (userInfos.userCategory !== 'individual' && userInfos.hasOwnProperty('thermalPanelSurface') &&
            this._params.hasOwnProperty('numberOfInhabitantsPerSolarPanelSurface')) {
            panelSurface = userInfos.thermalPanelSurface;
            nbInhabitants = this._params.numberOfInhabitantsPerSolarPanelSurface * panelSurface;
        } else {
            panelSurface = userInfos.numberOfInhabitants * this._params.solarPanelSurfacePerInhabitant;
            nbInhabitants = userInfos.numberOfInhabitants;
        }

        // Correct the surface of the solar panels to take the size of one panel into account
        var panelSurfaceUnit = 2; // m² (size of one thermal solar panel)
        var maxPanelSurface = this._roofModel.getProperty("maxAvailablePanelSurface").thermal;
        panelSurface = Math.round(Math.max(panelSurfaceUnit, panelSurface) / panelSurfaceUnit) * panelSurfaceUnit; // make the panel surface a multiple of panelSurfaceUnit
        if (panelSurface > maxPanelSurface) {
            panelSurface = Math.floor(maxPanelSurface / panelSurfaceUnit) * panelSurfaceUnit;
        }

        // Compute the economical figures
        this._roofModel.computeAnnualIrradiation({
            "panelSurface": panelSurface,
            "isFlatRoof": userInfos.hasOwnProperty('isFlatRoof') && userInfos.isFlatRoof
        }, "thermal");
        var annualSolarPotential = this._roofModel.getProperty("totalAnnualSolarEnergy");
        var coveredRequirements = this._computeCoveredRequirements(panelSurface, annualSolarPotential, nbInhabitants); // [kWh]
        var economicalFigures = this._computeEconomicalParameters(panelSurface, coveredRequirements, userInfos);

        // Store the thermal panel installation characteristics
        this._properties.thermalPanelSurface = panelSurface;
        this._properties.thermalPanelObservationPeriod = this._params.economics_hotWater.observationYears;
        this._properties.annualHotWaterVolumeProduction = coveredRequirements.volume; // in liters
        this._properties.dailyHotWaterVolumeProduction = coveredRequirements.volume / 365.25; // in liters
        this._properties.annualHeatProduction = coveredRequirements.energy; // in kWh/year
        this._properties.annualNeedsCoverageRate = coveredRequirements.volume / (this._params.dailyHotWaterConsumptionPerInhabitant * 365.25 * userInfos.numberOfInhabitants);

        this._properties['economicsResults'] = {
            'thermal' : economicalFigures
        };

        // this._properties['economicsResults']['thermal']['thermalPanelSurface'] = panelSurface;
        this._properties['economicsResults']['thermal']['thermalPanelObservationPeriod'] = this._params.economics_hotWater.observationYears;

        if (this._params.hasOwnProperty('geqCO2savedPerKWh')) {
            this._properties.thermalPanelAnnualCO2Savings = Math.round(coveredRequirements.energy / this._params.electricalBoilerEfficiency *
                this._params.geqCO2savedPerKWh * 1e-3);
        }
    };

    /**
     * Returns the properties of the thermal panel installation
     *
     * @return {Objet} - Installation properties. This object has the following keys:
     *      - annualHotWaterProduction: [L] Annual hot water production
     *      - hotWaterProductionEquivalent: [-] Number of persons whose annual hot water consumption is covered by the production
     *      - thermalPanelSurface: [m2] Surface of thermal panels to be installed
     *      - thermalPanelInstallationCost: [euros] Cost of the installation of the thermal panels
     *      - thermalPanelYearlySavings: [euros] Savings on the annual hot water bill due to the installation of the solar panels
     */
    module.ThermalPanelInstallation.prototype.getProperties = function() {
        return this._properties;
    };

    /**
     * Returns the specified property of the thermal panel installation
     *
     * @param {String} - Name of the property to be returned
     * @param {Number|String} - Value of the requested property
     */
    module.ThermalPanelInstallation.prototype.getProperty = function(property) {
        return this._properties[property];
    };

    /**
     * Compute the economical figures of the panel installation project
     *
     */
    module.ThermalPanelInstallation.prototype._computeEconomicalParameters = function(solarPanelSurfaceArea, coveredRequirements, userInfos) {
        var params = this._params.economics_hotWater;

        // Compute the installation cost => Raw Investment = project cost = SUM of the costs
        var fRawInvest = this._computeInstallationCost(solarPanelSurfaceArea, userInfos.userCategory);

        // Take extra local subsidy schemes into account
        var fExtraSubsidy = 0.;
        var extraSubsidyScheme = this._params.economics_hotWater.extraSubventionScheme;
        if (extraSubsidyScheme !== undefined && extraSubsidyScheme !== "") {

            if (extraSubsidyScheme === "Bordeaux Metropole") {
                fExtraSubsidy = Math.min(1000.0, fRawInvest * 0.2);
            } else {
                throw new Error("Unknown extra subsidy scheme '" + extraSubsidyScheme + "'");
            }

        }

        // Ticket #3998 : Compute the tax credit (credit d'impot)
        var taxCreditInfo = params.taxCreditRate[userInfos.userCategory];
        var totalSubvention = Math.min(taxCreditInfo.rate * fRawInvest, taxCreditInfo.maxTaxCredit) + fExtraSubsidy;


        // Ticket #3998 : Net Investment = Raw Investment - Total Subventions
        var fInitInvest = fRawInvest - totalSubvention;

        // ================================
        // ==== Procesing of the credit ===
        // ================================

        var simulateBankLoan = userInfos.hasOwnProperty('simulateBankLoan') ? userInfos.simulateBankLoan : true;

        // Compute the personal funds and the borrowed amount
        var borrowedAmountPercentage, fInvestBorrowed, fOwnFunds;
        if (simulateBankLoan) {
            borrowedAmountPercentage =  params.creditInitialAmountPercentage || 0;
            if ((userInfos.thermalPanelBorrowedPercentage >= 0) && (userInfos.thermalPanelBorrowedPercentage <= 100)) {
                borrowedAmountPercentage = userInfos.thermalPanelBorrowedPercentage / 100;//#2311
            }
            fInvestBorrowed = fInitInvest * borrowedAmountPercentage;//[75% of fInitInvest]
            fOwnFunds = fInitInvest - fInvestBorrowed ;
            if (userInfos.hasOwnProperty('creditPersonalFunds')) {
                fOwnFunds = Math.min(userInfos.creditPersonalFunds, fInitInvest);
                fInvestBorrowed = fInitInvest - fOwnFunds;
                borrowedAmountPercentage = fInvestBorrowed / fInitInvest;
            }
        } else {
            fOwnFunds = fInitInvest;
            fInvestBorrowed = 0;
            borrowedAmountPercentage = 0;
        }

        // converting the annual rate % into a numeric monthly rate
        var fCreditYearlyRate = params.hasOwnProperty('creditYearlyRate') ? params.creditYearlyRate : 3; // 3% default value
        var fCreditMonthlyRate = fCreditYearlyRate / 1200. ; // monthly rate

        var fCreditDurationInYears = params.hasOwnProperty('creditDurationInYears') ? params.creditDurationInYears : 20; // default to 20 years
        var fCreditDurationInMonths = fCreditDurationInYears * 12;

        var _fCoef = Math.pow(1.+fCreditMonthlyRate, fCreditDurationInMonths) ;

        var _nMonthlyRepayment;
        if ( fCreditMonthlyRate > 1.e-7 )
            _nMonthlyRepayment = fInvestBorrowed * fCreditMonthlyRate * _fCoef / (_fCoef-1.) ;
        else
            _nMonthlyRepayment = fInvestBorrowed / fCreditDurationInMonths ;

        // Ticket #3998 : var capital = fInitInvest ;
        var _nPaidAmount = fCreditDurationInMonths*_nMonthlyRepayment ;
        var _nInterestAmount = _nPaidAmount-fInvestBorrowed ;

        // var _nCashAmount = fOwnFunds / fCreditDurationInMonths ;

        var _nEndingCapital = Math.round(this.capital_fin( fInvestBorrowed, _nMonthlyRepayment, fCreditMonthlyRate, fCreditDurationInMonths )) ;

        // console.log('==================================');
        // console.log('pourcentage emprunte', borrowedAmountPercentage);
        // console.log('capital total', installationCost);
        // console.log('capital apport', installationCost - fInvestBorrowed);
        // console.log('capital emprunt', fInvestBorrowed);
        // console.log('remboursement annuel cash', _nCashAmount * 12);
        // console.log('remboursement annuel credit', _nMonthlyRepayment * 12);
        // console.log('remboursement annuel total', (_nMonthlyRepayment + _nCashAmount) * 12);
        // console.log('montant_verse', _nPaidAmount);
        // console.log('montant_interet', _nInterestAmount);
        // console.log('[verification] capital_fin', _nEndingCapital);
        // console.log('==================================');

        // Compute the annual maintenance cost
        var fAnnualMaintenanceCost = 0;
        for (var iThreshold = 0, nThresholds = params.maintenanceCost.variablePart.length; iThreshold < nThresholds; ++iThreshold) {
            var threshold = params.maintenanceCost.variablePart[iThreshold];
            var thresholdSurface = (threshold.maxSurface == null) ? Infinity : threshold.maxSurface;
            if (solarPanelSurfaceArea <= thresholdSurface) {
                fAnnualMaintenanceCost = threshold.annualCostPerM2 * solarPanelSurfaceArea;
                break;
            }
        }
        fAnnualMaintenanceCost = Math.max(params.maintenanceCost.minValue, fAnnualMaintenanceCost);

        // Compute the annual savings on the electricity bill
        var electricityPurchasePrice = this._params.economics_pv.buyElecPrice_HTVA[userInfos.userCategory] * (1 + this._params.economics_pv.elecTvaRate[userInfos.userCategory]); // euros/kWh
        var annualMoneySavings = userInfos.hasElectricalBoiler ? (coveredRequirements.energy * electricityPurchasePrice / this._params.electricalBoilerEfficiency) : 0.;

        // Compute the net profit at the end of the observation period
        var gainAnnualFactor = (1 + this._params.economics_pv.buyElecPriceInflation) / (1 + this._params.economics_global.actualisationRate);
        var gainTotalFactor = (gainAnnualFactor == 1) ? params.observationYears : (Math.pow(gainAnnualFactor, params.observationYears + 1) - gainAnnualFactor) / (gainAnnualFactor - 1);
        var fBillSavingsOverPeriod = annualMoneySavings * gainTotalFactor;
        var costAnnualFactor = (1 + this._params.economics_global.inflation) / (1 + this._params.economics_global.actualisationRate);
        var costTotalFactor = (costAnnualFactor == 1) ? params.observationYears : (Math.pow(costAnnualFactor, params.observationYears + 1) - costAnnualFactor) / (costAnnualFactor - 1);
        var maintenanceCostOverPeriod = fAnnualMaintenanceCost * costTotalFactor;

        var fIncomeOverPeriod = 0;

        // Ticket #3998 : Raw Profit = (Income + Savings)
        var fRawProfitOverPeriod = fIncomeOverPeriod + fBillSavingsOverPeriod;

        // Ticket #3998 : Spendings = Maintenance + Costs + repayment loan
        var fSpendingsOverPeriod = maintenanceCostOverPeriod + _nPaidAmount;

        // Ticket #3998 : Net incomings = Raw Profit - Spendings
        var fNetIncomingOverPeriod = fRawProfitOverPeriod - fSpendingsOverPeriod;

        // Ticket #3998 : Net Profit = Raw Profit - Spendings
        var fNetProfitOverPeriod = fNetIncomingOverPeriod - fOwnFunds;

        // Ticket #3998 : ROI : Own funds * Duration / net profit
        var fROI = (fNetProfitOverPeriod <=0) ? Number.POSITIVE_INFINITY : (fOwnFunds) / Math.max(fNetIncomingOverPeriod, 0) * params.observationYears;

        // Taux de rentabilité d'un investissement ayant la même VAN que le projet solaire
        var rateOfReturn = Math.pow(Math.max(1, (fRawProfitOverPeriod - maintenanceCostOverPeriod - _nInterestAmount) / fInitInvest), 1 / params.observationYears) - 1;


        return {
            rawInstallationCost:               fRawInvest,
            installationCost:                  fInitInvest,
            borrowedAmount:                    fInvestBorrowed,
            borrowedPercentage:                borrowedAmountPercentage,
            creditCost:                        _nInterestAmount,
            creditAmount:                      _nPaidAmount,
            creditPeriod:                      fCreditDurationInYears,
            ownFunds:                          fOwnFunds,
            spendingsOverPeriod:               fSpendingsOverPeriod,
            billSavingsOverPeriod:             fBillSavingsOverPeriod,
            incomingOverPeriod:                fIncomeOverPeriod,
            maintenanceCostOverPeriod:         maintenanceCostOverPeriod,
            rawProfitOverPeriod:               Math.max(fRawProfitOverPeriod, 0),
            netProfitOverPeriod:               Math.max(fNetProfitOverPeriod, 0), // in euros
            totalSubventionOverPeriod:         totalSubvention,
            returnOnInvestment:                fROI,
            observationPeriod:                 params.observationYears,
            rateOfReturn:                      rateOfReturn
        };
    };

    module.ThermalPanelInstallation.prototype.capital_fin = function( c0, m, t, n ) {
        var cn;
        if ( t > 1.e-7 ) {
            var coef = Math.pow(1.+t,n) ;
            cn = coef*(c0-m/t) + m/t ;
        } else {
            // coef ~ 1+n*t
            cn = c0 -n*m ;
        }
        return cn ;
    }

    /**
     * Returns the covered energy requirements in terms of hot water heating for the entire household (i.e. all the persons in the house,
     * not only one person)
     *
     * @param {Number} solarPanelSurfaceArea - [m²] Surface area of the solar thermal panels
     * @param {Number} annualSolarPotential - [kWh] Cumulated incoming solar energy over the entire year, over the surface area of the solar panels
     * @param {Number} nPersons - [-] Number of persons in the household
     * @return - [kWh] Energy requirements covered by the solar thermal panels
     */
    module.ThermalPanelInstallation.prototype._computeCoveredRequirements = function(solarPanelSurfaceArea, annualSolarPotential, nPersons) {
        var coveredRequirements = {
            energy: 0,
            volume: 0
        };

        var k1 = 3.8;  // Fr.Ul en W/m²/k
        var k2 = 0.97; // Fr'/Fr
        var k3 = 0.74; // Fr.(tau_alpha)
        var k4 = 0.96; // tau.alpha/(tau.alpha)n
        var referenceTemp = 100; // degrees Celsius
        for (var iMonth = 0; iMonth < 12; ++iMonth) {
            var monthlyMeanAirTemperature = this._params.monthlyMeanAirTemperature[iMonth];
            var monthlyMeanEnergyRequirements = this._params.monthlyMeanEnergyRequirements[iMonth] * nPersons;
            var baseFact = k1 * k2 * (referenceTemp - monthlyMeanAirTemperature) * this._params.numberOfDaysPerMonth[iMonth] * 24 * solarPanelSurfaceArea / (monthlyMeanEnergyRequirements * 1000);
            var corrFact = (11.6 + 1.18 * this._params.waterHeatingTemperature + 3.86 * this._params.annualMeanAirTemperature - 2.32 * monthlyMeanAirTemperature) / (100 - monthlyMeanAirTemperature);
            var fact1 = baseFact * corrFact;
            var fact2 = k2 * k3 * k4 * this._params.annualToMonthlySolarPotential[iMonth] * annualSolarPotential / monthlyMeanEnergyRequirements;
            
            var coverageRate = 1.;
            if (fact1 <= 18 && fact2 < 3) {
                coverageRate = Math.min(1, 1.029 * fact2 - 0.065 * fact1 - 0.245 * fact2 * fact2 + 0.0018 * fact1 * fact1 + 0.0215 * fact2 * fact2 * fact2);
            }

            coveredRequirements.energy += monthlyMeanEnergyRequirements * coverageRate;
            coveredRequirements.volume += this._params.numberOfDaysPerMonth[iMonth] * this._params.dailyHotWaterConsumptionPerInhabitant * nPersons * coverageRate;
        }

        return coveredRequirements;
    };

    /**
     * Returns the price to pay in order to install the given surface of thermal solar panels
     *
     * @param {Number} solarPanelSurfaceArea - [m²] Surface area of the solar thermal panels to be installed
     * @param {String} userType - [individual|professional|administration] Type of user
     * @returns {Number} Price to pay in order to install the solar panels, in euros
     */
    module.ThermalPanelInstallation.prototype._computeInstallationCost = function(solarPanelSurfaceArea, userType) {
        var params = this._params.economics_hotWater;

        // Compute the installation cost
        var installationCost = 0;
        if (Array.isArray(params.panelInstallationCost)) { // original computation method (price prop. to the surface, with price per m² depending on the installation size)
            var surfaces = [];
            var costs = [];
            params.panelInstallationCost.forEach(function(threshold) {
                surfaces.push(threshold.surface);
                costs.push(threshold.costPerM2);
            });
            var specificCost = am.utils.interpolateLinearly(solarPanelSurfaceArea, surfaces, costs);
            installationCost = specificCost * solarPanelSurfaceArea;
        } else { // computation method from Narbonne (fixed part + variable part prop. to the surface)
            var installationCost = params.panelInstallationCost.fixedPart + params.panelInstallationCost.variablePart * solarPanelSurfaceArea;
        }

        // Compute the tax credit (credit d'impot)
        // var taxCreditInfo = params.taxCreditRate[userType];
        // var taxCredit = Math.min(taxCreditInfo.rate * installationCost, taxCreditInfo.maxTaxCredit);



        return Math.max(0., installationCost);
    };

    return module;

})(am || {});


//=============================================
// AM.UTILS NAMESPACE
//=============================================
am.utils = (function(module) {

    // Use strict mode
    'use strict';

    /**
     * Returns a new instance of class am.ThermalPanelInstallation
     *
     * @param {am.ModelBaseClass} roofModel - Reference to the SolarPotentialModel instance to be used in order to 
     *      compute the solar potential of the PV panel installation.
     * @param {Object} params - Object configuration (see constructor of class am.ThermalPanelInstallation for more information)
     * @return {am.ThermalPanelInstallation} - New instance of class am.ThermalPanelInstallation 
     */
    module.thermalPanelInstallation = function(roofModel, params) {
        var model = new am.ThermalPanelInstallation();
        model.init(roofModel, params);
        return model;
    };

    return module;

})(am.utils || {});